/**
 * Auth Provider
 * 
 * Feature: 033-auth-complete
 * Description: React context provider for authentication with session monitoring
 * Constitution: v1.11.0
 */

import React, { useEffect, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import { SessionInfo, PermissionsResponse } from '@shared/types/auth';
import { NO_LICENSES } from '@shared/access/licenses';
import { useSessionMonitor } from './hooks/useSessionMonitor';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { SessionExpiryModal } from '../components/auth/SessionExpiryModal';
import { setupAuthInterceptor } from '../services/apiClient';
import { apiTokenRequest } from './config';
import { devRoleHeaders } from './devUserStore';
import { applySessionLicence } from './licenceSession';
import { applyAccountRefusal } from './accountRefusal';
import { AccountArchivedPage } from '../pages/auth/AccountArchivedPage';

interface AuthProviderProps {
  msalInstance: IPublicClientApplication;
  children: React.ReactNode;
}

/**
 * Auth Provider component with session monitoring
 * Wraps the app with MSAL context and manages auth state
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ msalInstance, children }) => {
  const { accountArchived, setUser, setPermissions, setLicenses, setLoading, setError, isAuthenticated, logout } = useAuthStore();
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Session monitoring (idle timeout tracking)
  const { updateActivity } = useSessionMonitor({
    onIdleWarning: (seconds) => {
      setRemainingSeconds(seconds);
      setShowExpiryModal(true);
    },
    onIdleTimeout: () => {
      setShowExpiryModal(false);
      logout();
    },
  });

  // Token refresh (proactive token renewal)
  useTokenRefresh();

  useEffect(() => {
    // Setup API client interceptor with MSAL instance and API scopes
    setupAuthInterceptor(msalInstance, apiTokenRequest);

    const initializeAuth = async () => {
      try {
        setLoading(true);

        // Check if user is authenticated with MSAL
        // 🛑 No sign-in diagnostics are posted to the backend. The old auth "diag" endpoint was
        //    anonymous and logged this state (the user's email and the full page URL) at info
        //    level; it was removed 2026-09-30 and noAnonymousAuthDiag.guard.test.ts keeps it out.
        //    Diagnose sign-in in the browser console instead.
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length === 0) {
          // No MSAL account — try a cookieless session fetch (succeeds in dev-bypass mode).
          // In dev-bypass the backend takes the role from X-Dev-User-Role (default Admin), so
          // both calls carry the picked role — without it every session read back as Admin.
          try {
            const sessionResponse = await axios.get<SessionInfo>('/api/auth/session', { headers: devRoleHeaders() });
            setUser(sessionResponse.data.user);
            // Spec 285/286 — the viewing tenant's licence and its add-ons (fail-closed when absent).
            applySessionLicence(sessionResponse.data);
            // Permissions failure must not clear the user — keep them authenticated
            try {
              const permissionsResponse = await axios.get<PermissionsResponse>('/api/auth/permissions', { headers: devRoleHeaders() });
              setPermissions(permissionsResponse.data);
            } catch {
              setPermissions(null);
            }
          } catch {
            setUser(null);
            setPermissions(null);
            setLicenses(NO_LICENSES);
          }
          setLoading(false);
          return;
        }

        // Set active account
        msalInstance.setActiveAccount(accounts[0]);
        console.info('[AuthProvider] Using account:', accounts[0].username);

        // Get access token for backend API (must use API scopes, not Graph scopes)
        let tokenResponse;
        try {
          tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: apiTokenRequest.scopes,
            account: accounts[0],
          });
        } catch (tokenError: any) {
          // If silent fails (consent needed, interaction required), redirect for consent
          if (tokenError.errorCode === 'consent_required' ||
              tokenError.errorCode === 'interaction_required' ||
              tokenError.errorCode === 'login_required') {
            console.warn('[AuthProvider] Interactive consent needed for API scope, redirecting...');
            await msalInstance.acquireTokenRedirect({
              scopes: apiTokenRequest.scopes,
              account: accounts[0],
            });
            return; // Page will redirect, stop execution
          }
          throw tokenError;
        }
        console.info('[AuthProvider] API token acquired, calling backend...');

        // Set authorization header for API calls. 🛑 ID token (FCM model) — see auth/config.ts:
        //    OIDC scopes cannot mint a custom-API access token for a personal/foreign account.
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokenResponse.idToken}`;

        // Fetch session info from backend
        const sessionResponse = await axios.get<SessionInfo>('/api/auth/session');
        setUser(sessionResponse.data.user);
        // Spec 285/286 — the viewing tenant's licence and its add-ons (fail-closed when absent).
        applySessionLicence(sessionResponse.data);

        // Fetch permissions
        const permissionsResponse = await axios.get<PermissionsResponse>('/api/auth/permissions');
        setPermissions(permissionsResponse.data);

        setLoading(false);
      } catch (error: any) {
        console.error('Auth initialization error:', error);

        // An archived person is told why, so this refusal is not signed out: the redirect would take the
        // message with it (owner, 2026-10-31).
        if (applyAccountRefusal(error)) {
          setLoading(false);
          return;
        }

        // If API call fails, try to logout and clear state
        if (error.response?.status === 401 || error.response?.status === 403) {
          setUser(null);
          setPermissions(null);
          setLicenses(NO_LICENSES);
          await msalInstance.logoutRedirect();
        } else {
          setError(error.message || 'Failed to initialize authentication');
        }
        
        setLoading(false);
      }
    };

    initializeAuth();
  }, [msalInstance, setUser, setPermissions, setLoading, setError]);

  /**
   * Handle session extension (user clicked "Continue Working")
   */
  const handleExtendSession = () => {
    updateActivity();
    setShowExpiryModal(false);
  };

  /**
   * Handle manual logout from modal
   */
  const handleLogoutFromModal = () => {
    setShowExpiryModal(false);
    logout();
  };

  return (
    <MsalProvider instance={msalInstance}>
      {accountArchived ? <AccountArchivedPage onSignOut={() => { void msalInstance.logoutRedirect(); }} /> : children}
      <SessionExpiryModal
        show={showExpiryModal}
        remainingSeconds={remainingSeconds}
        onExtend={handleExtendSession}
        onLogout={handleLogoutFromModal}
      />
    </MsalProvider>
  );
};
